import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Calendar,
  Truck, 
  Users,
  Save,
  AlertCircle,
  CheckCircle,
  Clock,
  Edit,
  RotateCcw,
  Copy,
  Trash2,
  Plus,
  Upload,
  Download,
  FileSpreadsheet,
  Activity,
  UserCheck,
  UserX
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';

import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';

// Türkiye saatine göre bugünün tarihini döndürür
const getTurkeyDate = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' }); // YYYY-MM-DD formatında
};

const DailyTeamAssignmentPage = () => {
  const { user } = useAuthStore();
  const canManageAssignments = ['admin', 'super_admin', 'supervisor', 'team_leader', 'operator'].includes(user?.role);
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(getTurkeyDate());
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({});
  const [copyFromDate, setCopyFromDate] = useState('');
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  
  // Excel import/export states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [exportStartDate, setExportStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [exportEndDate, setExportEndDate] = useState(new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]);
  const fileInputRef = useRef(null);
  
  // Team status states
  const [showTeamStatus, setShowTeamStatus] = useState(false);

  // Fetch daily team assignments
  const { data: teams, isLoading, error } = useQuery({
    queryKey: ['teams', 'daily', selectedDate],
    queryFn: () => api.get(`/teams/daily?date=${selectedDate}`).then(res => res.data.data),
  });

  // Fetch team status
  const { data: teamStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['teams', 'status', selectedDate],
    queryFn: () => api.get(`/teams/status?date=${selectedDate}`).then(res => res.data),
    enabled: showTeamStatus,
  });

  // O günün nöbet planı: görev yeri (team_code) -> çalışan personel
  const { data: dayPlan = {} } = useQuery({
    queryKey: ['puantaj', 'day-teams', selectedDate],
    queryFn: () => api.get(`/puantaj/day-teams?date=${selectedDate}`).then(res => res.data?.data?.by_team || {}),
  });

  // Update team assignment mutation
  const updateAssignmentMutation = useMutation({
    mutationFn: ({ teamId, data }) => 
      api.put(`/teams/${teamId}/daily/${selectedDate}`, data),
    onSuccess: () => {
      // Günlük atama ve ilgili tüm cache'leri invalidate et
      queryClient.invalidateQueries(['teams', 'daily', selectedDate]);
      queryClient.invalidateQueries(['teams', 'status', selectedDate]);
      queryClient.invalidateQueries(['teams']); // Genel takım listesi
      queryClient.invalidateQueries(['cases']); // Vaka listesi (atanan takım bilgisi için)
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teamAssignments']); // Takım atamaları
      setEditingTeam(null);
      setFormData({});
      toast({
        title: "Başarılı",
        description: "Ekip ataması güncellendi.",
        variant: "success"
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Güncelleme hatası",
        variant: "destructive"
      });
    }
  });

  // Reset all assignments for selected date
  const resetAssignmentsMutation = useMutation({
    mutationFn: (date) => api.delete(`/teams/daily/${date}/reset`),
    onSuccess: (response) => {
      // Reset sonrası tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['teams', 'daily', selectedDate]);
      queryClient.invalidateQueries(['teams', 'status', selectedDate]);
      queryClient.invalidateQueries(['teams']); // Genel takım listesi
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teamAssignments']); // Takım atamaları
      toast({
        title: "Başarılı",
        description: response.data.message || "Tüm ekip atamaları silindi.",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Atamalar silinemedi",
        variant: "destructive"
      });
    }
  });

  // Copy assignments from one date to another
  const copyAssignmentsMutation = useMutation({
    mutationFn: ({ sourceDate, targetDate }) => 
      api.post(`/teams/daily/${sourceDate}/copy-to/${targetDate}`),
    onSuccess: (response) => {
      // Copy sonrası tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['teams', 'daily', selectedDate]);
      queryClient.invalidateQueries(['teams', 'status', selectedDate]);
      queryClient.invalidateQueries(['teams']); // Genel takım listesi
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teamAssignments']); // Takım atamaları
      setShowCopyDialog(false);
      setCopyFromDate('');
      toast({
        title: "Başarılı",
        description: response.data.message || "Atamalar kopyalandı.",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Atamalar kopyalanamadı",
        variant: "destructive"
      });
    }
  });

  // Excel import mutation
  const importExcelMutation = useMutation({
    mutationFn: (formData) => {
      return api.post('/teams/daily/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: (response) => {
      // Excel import sonrası tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['teams', 'daily', selectedDate]);
      queryClient.invalidateQueries(['teams', 'status', selectedDate]);
      queryClient.invalidateQueries(['teams']); // Genel takım listesi
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      queryClient.invalidateQueries(['teamAssignments']); // Takım atamaları
      setShowImportDialog(false);
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast({
        title: "Başarılı",
        description: response.data.message || "Excel dosyası başarıyla yüklendi",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Excel yükleme hatası",
        variant: "destructive"
      });
    }
  });

  const handleEdit = (team) => {
    if (!canManageAssignments) return;
    setEditingTeam(team.team_id);
    // Mevcut personel listesi; yoksa görev yerindeki (nöbet planı) personelden doldur
    let staff = (team.staff_names && team.staff_names.length)
      ? [...team.staff_names]
      : [team.staff_1_name, team.staff_2_name, team.staff_3_name].filter(Boolean);
    if (!staff.length) {
      const plan = dayPlan[team.team_code] || [];
      staff = plan.map((p) => p.name);
    }
    if (staff.length < 2) staff = [...staff, ...Array(2 - staff.length).fill('')];
    setFormData({ ambulance_plate: team.ambulance_plate || '', staff });
  };

  const handleSave = (teamId) => {
    if (!canManageAssignments) return;
    updateAssignmentMutation.mutate({
      teamId,
      data: {
        ambulance_plate: formData.ambulance_plate || '',
        staff_names: (formData.staff || []).map((s) => (s || '').trim()).filter(Boolean),
      },
    });
  };

  const setStaffAt = (idx, val) =>
    setFormData((prev) => ({ ...prev, staff: prev.staff.map((s, i) => (i === idx ? val : s)) }));
  const addStaffRow = () => setFormData((prev) => ({ ...prev, staff: [...(prev.staff || []), ''] }));
  const removeStaffRow = (idx) =>
    setFormData((prev) => ({ ...prev, staff: prev.staff.filter((_, i) => i !== idx) }));
  const fillFromPlan = (teamCode) => {
    const plan = dayPlan[teamCode] || [];
    setFormData((prev) => ({ ...prev, staff: plan.length ? plan.map((p) => p.name) : (prev.staff || ['']) }));
  };

  const handleCancel = () => {
    if (!canManageAssignments) return;
    setEditingTeam(null);
    setFormData({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetAssignments = () => {
    if (!canManageAssignments) return;
    if (window.confirm(`${selectedDate} tarihi için tüm ekip atamalarını silmek istediğinizden emin misiniz?`)) {
      resetAssignmentsMutation.mutate(selectedDate);
    }
  };

  const handleCopyAssignments = () => {
    if (!canManageAssignments) return;
    if (!copyFromDate) {
      toast({
        title: "Hata",
        description: "Kaynak tarih seçmelisiniz",
        variant: "destructive"
      });
      return;
    }
    
    if (copyFromDate === selectedDate) {
      toast({
        title: "Hata", 
        description: "Kaynak ve hedef tarih aynı olamaz",
        variant: "destructive"
      });
      return;
    }

    copyAssignmentsMutation.mutate({
      sourceDate: copyFromDate,
      targetDate: selectedDate
    });
  };

  // Excel import/export handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleImportExcel = () => {
    if (!canManageAssignments) return;
    if (!importFile) {
      toast({
        title: "Hata",
        description: "Excel dosyası seçmelisiniz",
        variant: "destructive"
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);
    importExcelMutation.mutate(formData);
  };

  const handleExportExcel = async () => {
    if (!canManageAssignments) return;
    if (!exportStartDate || !exportEndDate) {
      toast({
        title: "Hata",
        description: "Başlangıç ve bitiş tarihi seçmelisiniz",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await api.get(`/teams/daily/export?startDate=${exportStartDate}&endDate=${exportEndDate}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gunluk_atamalar_${exportStartDate}_${exportEndDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowExportDialog(false);
      toast({
        title: "Başarılı",
        description: "Excel dosyası indirildi",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Excel export hatası",
        variant: "destructive"
      });
    }
  };

  const handleDownloadTemplate = async () => {
    if (!canManageAssignments) return;
    try {
      const response = await api.get(`/teams/daily/template?date=${selectedDate}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gunluk_atamalar_sablon_${selectedDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Başarılı",
        description: "Excel şablonu indirildi. Bu şablonu doldurup yükleyebilirsiniz.",
        variant: "success"
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Şablon indirme hatası",
        variant: "destructive"
      });
    }
  };

  // Doğal sıralama: önce harf grubuna (M, T vb.), sonra numaraya göre
  const naturalSort = (a, b) => {
    const codeA = a.team_code || '';
    const codeB = b.team_code || '';
    const prefixA = (codeA.match(/^[A-Za-zÇçĞğİıÖöŞşÜü]+/) || [''])[0].toUpperCase();
    const prefixB = (codeB.match(/^[A-Za-zÇçĞğİıÖöŞşÜü]+/) || [''])[0].toUpperCase();
    if (prefixA !== prefixB) return prefixA.localeCompare(prefixB, 'tr');
    const numA = parseInt((codeA.match(/\d+/) || ['0'])[0], 10);
    const numB = parseInt((codeB.match(/\d+/) || ['0'])[0], 10);
    return numA - numB;
  };

  // Ensure teams is an array before filtering, then sort naturally
  const teamsData = (Array.isArray(teams) ? [...teams] : []).sort(naturalSort);
  const assignedTeams = teamsData.filter(team => team.ambulance_plate) || [];
  const unassignedTeams = teamsData.filter(team => !team.ambulance_plate) || [];

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-700">Veri Yüklenirken Hata Oluştu</h3>
              <p className="text-red-600 mt-2">{error.message}</p>
              <Button 
                onClick={() => window.location.reload()} 
                className="mt-4"
                variant="outline"
              >
                Yeniden Dene
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Günlük Ekip Atamaları</h1>
        <p className="text-muted-foreground">
          Ambulans plakaları ve personel atamalarını günlük olarak yönetin
        </p>
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
          Atamalar her gün <strong className="mx-0.5">07:40</strong> (Türkiye saati) otomatik olarak sıfırlanır.
        </div>
      </div>

      {/* Date Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Tarih Seçimi
            </div>
            {canManageAssignments && (
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCopyDialog(true)}
                  disabled={copyAssignmentsMutation.isPending}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Kopyala
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetAssignments}
                  disabled={resetAssignmentsMutation.isPending || assignedTeams.length === 0}
                  className="text-red-600 hover:text-red-700"
                >
                  {resetAssignmentsMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Tümünü Sil
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="space-y-2">
              <Label htmlFor="date">Atama Tarihi</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto"
              />
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span>Atanmış: {assignedTeams.length}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span>Atanmamış: {unassignedTeams.length}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>İşlemler</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            {canManageAssignments && (
              <>
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2 border-green-200 text-green-700 hover:bg-green-50"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>Şablon İndir</span>
                </Button>
                
                <Button
                  onClick={() => setShowImportDialog(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <Upload className="h-4 w-4" />
                  <span>Excel Yükle</span>
                </Button>
                
                <Button
                  onClick={() => setShowExportDialog(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Excel İndir</span>
                </Button>
                
                <Button
                  onClick={() => setShowCopyDialog(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <Copy className="h-4 w-4" />
                  <span>Atama Kopyala</span>
                </Button>
                
                <Button
                  onClick={handleResetAssignments}
                  variant="destructive"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Tümünü Sil</span>
                </Button>
              </>
            )}
            
            <Button
              onClick={() => setShowTeamStatus(!showTeamStatus)}
              variant={showTeamStatus ? "default" : "outline"}
              size="sm"
              className="flex items-center space-x-2"
            >
              <Activity className="h-4 w-4" />
              <span>Ekip Durumu {showTeamStatus ? 'Gizle' : 'Göster'}</span>
            </Button>
          </div>
          {!canManageAssignments && (
            <p className="text-xs text-muted-foreground mt-2">
              Bu bölümdeki işlemler sadece admin ve supervisor rollerine açıktır.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Excel Import Dialog */}
      {canManageAssignments && showImportDialog && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-700">
              <Upload className="h-5 w-5 mr-2" />
              Excel Dosyası Yükle
            </CardTitle>
            <CardDescription>
              "Şablon İndir" ile Excel şablonunu indirin, ambulans plakası ve personel bilgilerini doldurun, sonra yükleyin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="file">Excel Dosyası</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mt-1"
                />
              </div>
              {importFile && (
                <div className="text-sm text-green-600">
                  Seçilen dosya: {importFile.name}
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  size="sm"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Şablon İndir
                </Button>
                <Button
                  onClick={handleImportExcel}
                  disabled={importExcelMutation.isPending || !importFile}
                  size="sm"
                >
                  {importExcelMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Yükle
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  size="sm"
                >
                  İptal
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Excel Export Dialog */}
      {canManageAssignments && showExportDialog && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center text-green-700">
              <Download className="h-5 w-5 mr-2" />
              Excel Dosyası İndir
            </CardTitle>
            <CardDescription>
              Belirtilen tarih aralığındaki atamaları Excel dosyası olarak indirin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="exportStartDate">Başlangıç Tarihi</Label>
                <Input
                  id="exportStartDate"
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="exportEndDate">Bitiş Tarihi</Label>
                <Input
                  id="exportEndDate"
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-4">
              <Button
                onClick={handleExportExcel}
                disabled={!exportStartDate || !exportEndDate}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                İndir
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowExportDialog(false)}
                size="sm"
              >
                İptal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Status Card */}
      {showTeamStatus && teamStatus && (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center text-purple-700">
              <Activity className="h-5 w-5 mr-2" />
              Ekip Durumu ({selectedDate})
            </CardTitle>
            <CardDescription>
              Ekiplerin mevcut durumu ve aktif vakaları
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-white rounded-lg">
                <div className="text-2xl font-bold text-gray-800">{teamStatus.summary?.total || 0}</div>
                <div className="text-sm text-gray-600">Toplam Ekip</div>
              </div>
              <div className="text-center p-3 bg-green-100 rounded-lg">
                <div className="text-2xl font-bold text-green-800">{teamStatus.summary?.available || 0}</div>
                <div className="text-sm text-green-600">Müsait</div>
              </div>
              <div className="text-center p-3 bg-red-100 rounded-lg">
                <div className="text-2xl font-bold text-red-800">{teamStatus.summary?.busy || 0}</div>
                <div className="text-sm text-red-600">Görevde</div>
              </div>
              <div className="text-center p-3 bg-gray-100 rounded-lg">
                <div className="text-2xl font-bold text-gray-800">{teamStatus.summary?.unassigned || 0}</div>
                <div className="text-sm text-gray-600">Atanmamış</div>
              </div>
            </div>
            
            {teamStatus.data && teamStatus.data.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {[...(teamStatus.data || [])].sort((a, b) => {
                    const cA = a.team_code || '', cB = b.team_code || '';
                    const pA = (cA.match(/^[A-Za-zÇçĞğİıÖöŞşÜü]+/) || [''])[0].toUpperCase();
                    const pB = (cB.match(/^[A-Za-zÇçĞğİıÖöŞşÜü]+/) || [''])[0].toUpperCase();
                    if (pA !== pB) return pA.localeCompare(pB, 'tr');
                    return parseInt((cA.match(/\d+/) || ['0'])[0], 10) - parseInt((cB.match(/\d+/) || ['0'])[0], 10);
                  }).map((team) => (
                  <div key={team.team_id} className="flex items-center justify-between p-2 bg-white rounded text-sm">
                    <div className="flex items-center space-x-3">
                      <Badge 
                        variant={team.status === 'available' ? 'default' : team.status === 'busy' ? 'destructive' : 'secondary'}
                        className="min-w-16"
                      >
                        {team.status === 'available' ? 'Müsait' : team.status === 'busy' ? 'Görevde' : 'Atanmamış'}
                      </Badge>
                      <span className="font-medium">{team.team_code}</span>
                      {team.ambulance_plate && (
                        <span className="text-gray-600">({team.ambulance_plate})</span>
                      )}
                    </div>
                    <div className="text-right">
                      {team.active_cases > 0 && (
                        <div className="text-red-600 font-medium">
                          {team.active_cases} aktif vaka
                        </div>
                      )}
                      {team.case_details && team.case_details.length > 0 && (
                        <div className="text-xs text-gray-500">
                          {team.case_details.slice(0, 2).join(', ')}
                          {team.case_details.length > 2 && '...'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Copy Dialog */}
      {canManageAssignments && showCopyDialog && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-700">
              <Copy className="h-5 w-5 mr-2" />
              Ekip Atamalarını Kopyala
            </CardTitle>
            <CardDescription>
              Başka bir tarihten {selectedDate} tarihine ekip atamalarını kopyalayın
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <div className="space-y-2 flex-1">
                <Label htmlFor="copyFromDate">Kaynak Tarih</Label>
                <Input
                  id="copyFromDate"
                  type="date"
                  value={copyFromDate}
                  onChange={(e) => setCopyFromDate(e.target.value)}
                  className="w-auto"
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Button
                  onClick={handleCopyAssignments}
                  disabled={copyAssignmentsMutation.isPending || !copyFromDate}
                  size="sm"
                >
                  {copyAssignmentsMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Kopyala
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCopyDialog(false);
                    setCopyFromDate('');
                  }}
                  size="sm"
                >
                  İptal
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unassigned Teams Alert */}
      {unassignedTeams.length > 0 && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-700 font-medium">
                {unassignedTeams.length} ekip için atama yapılmamış
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamsData.map((team) => (
          <Card 
            key={team.team_id} 
            className={`transition-all ${
              !team.ambulance_plate 
                ? 'border-red-200 bg-red-50' 
                : 'border-green-200 bg-green-50'
            }`}
          >
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Truck className={`h-5 w-5 ${team.ambulance_plate ? 'text-green-600' : 'text-red-600'}`} />
                  <span>{team.team_code}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Assignment Status Badge */}
                  {team.ambulance_plate ? (
                    <Badge variant="default" className="bg-green-600">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Atanmış
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <Clock className="h-3 w-3 mr-1" />
                      Bekliyor
                    </Badge>
                  )}
                  
                  {/* Team Availability Status Badge */}
                  {showTeamStatus && teamStatus?.data && (() => {
                    const teamStatusInfo = teamStatus.data.find(t => t.team_id === team.team_id);
                    if (teamStatusInfo && teamStatusInfo.status === 'busy') {
                      return (
                        <Badge variant="destructive" className="text-xs">
                          <Activity className="h-3 w-3 mr-1" />
                          {teamStatusInfo.active_cases} Vaka
                        </Badge>
                      );
                    } else if (teamStatusInfo && teamStatusInfo.status === 'available') {
                      return (
                        <Badge variant="default" className="bg-blue-600 text-xs">
                          <UserCheck className="h-3 w-3 mr-1" />
                          Müsait
                        </Badge>
                      );
                    }
                    return null;
                  })()}
                  
                  {canManageAssignments && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(team)}
                      disabled={editingTeam === team.team_id}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                Mobil Ambulans Ekibi
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {editingTeam === team.team_id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Ambulans Plakası</Label>
                    <Input
                      value={formData.ambulance_plate}
                      onChange={(e) => handleInputChange('ambulance_plate', e.target.value)}
                      placeholder="34 ABC 123"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Personel ({(formData.staff || []).length})</Label>
                      {(dayPlan[team.team_code]?.length > 0) && (
                        <button
                          type="button"
                          onClick={() => fillFromPlan(team.team_code)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Plandan doldur ({dayPlan[team.team_code].length})
                        </button>
                      )}
                    </div>

                    {/* Görev yerindeki personel (nöbet planı) — bu ekibin datalist seçenekleri */}
                    <datalist id={`staff-options-${team.team_id}`}>
                      {(dayPlan[team.team_code] || []).map((p) => (
                        <option key={p.name} value={p.name}>{p.unvan || ''}</option>
                      ))}
                    </datalist>

                    {(formData.staff || []).map((s, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          list={`staff-options-${team.team_id}`}
                          value={s}
                          onChange={(e) => setStaffAt(idx, e.target.value)}
                          placeholder={`Personel ${idx + 1} (adından seçin)`}
                        />
                        <button
                          type="button"
                          onClick={() => removeStaffRow(idx)}
                          className="shrink-0 text-gray-400 hover:text-red-600"
                          title="Kaldır"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addStaffRow}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    >
                      <Plus className="h-3.5 w-3.5" /> Personel ekle
                    </button>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={() => handleSave(team.team_id)}
                      disabled={updateAssignmentMutation.isPending}
                    >
                      {updateAssignmentMutation.isPending ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Save className="h-3 w-3 mr-1" />
                          Kaydet
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancel}
                    >
                      İptal
                    </Button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-gray-500">Ambulans Plakası</Label>
                    <p className="font-medium">
                      {team.ambulance_plate || (
                        <span className="text-red-500 italic">Atanmamış</span>
                      )}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-gray-500 flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      Personel
                    </Label>
                    <div className="space-y-1 text-sm">
                      {(team.staff_names && team.staff_names.length) ? (
                        team.staff_names.map((n, i) => <p key={i}>{n}</p>)
                      ) : (
                        <span className="text-gray-400">Personel atanmamış</span>
                      )}
                    </div>
                    {/* Görev yerinde bugün çalışması planlanan personel (nöbet planı) */}
                    {dayPlan[team.team_code]?.length > 0 && (
                      <div className="mt-2 rounded-md bg-blue-50 px-2 py-1.5">
                        <p className="text-[11px] font-medium text-blue-700 mb-0.5">
                          Görev yerinde bugün ({dayPlan[team.team_code].length}):
                        </p>
                        <p className="text-[11px] text-blue-600 leading-snug">
                          {dayPlan[team.team_code].map((p) => p.name).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {team.updated_at && (
                    <div className="text-xs text-gray-500 pt-2 border-t">
                      Son güncelleme: {new Date(team.updated_at).toLocaleString('tr-TR')}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DailyTeamAssignmentPage; 